// @ts-nocheck
/**
 * Invitation Detail Modal
 *
 * Displays detailed information about a single invitation, including
 * header with recipient info, info grid, and a vertical event timeline.
 *
 * Uses framer-motion AnimatePresence for enter/exit animations.
 * Fetches invitation detail (with events) from the API when opened.
 *
 * Used by:
 * - InvitationTrackingTable (row click handler)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Loader2,
  Mail,
  CheckCircle,
  Eye,
  MousePointer,
  UserCheck,
  UserX,
  UserMinus,
  AlertTriangle,
  Bell,
  Truck,
  Clock,
} from 'lucide-react';
import { Button } from '@shared/components/ui';
import {
  InvitationStatusBadge,
  InvitationTypeBadge,
} from '@shared/components/InvitationStatusBadge';
import { invitationTrackingApi } from '@shared/api/invitationTrackingApi';
import type {
  InvitationRecord,
  InvitationEvent,
  InvitationEventType,
} from '@shared/types/invitationTracking';

// ============================================================
// PROPS
// ============================================================

interface InvitationDetailModalProps {
  invitationId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================
// EVENT ICON & LABEL CONFIG
// ============================================================

interface EventConfig {
  icon: React.FC<{ className?: string }>;
  label: string;
  color: string;
}

const eventConfig: Record<string, EventConfig> = {
  delivered: {
    icon: Truck,
    label: 'Delivered',
    color: 'text-green-400',
  },
  opened: {
    icon: Eye,
    label: 'Opened',
    color: 'text-emerald-400',
  },
  clicked: {
    icon: MousePointer,
    label: 'Clicked',
    color: 'text-teal-400',
  },
  rsvp_yes: {
    icon: UserCheck,
    label: 'RSVP: Yes',
    color: 'text-green-400',
  },
  rsvp_no: {
    icon: UserX,
    label: 'RSVP: No',
    color: 'text-red-400',
  },
  rsvp_maybe: {
    icon: UserMinus,
    label: 'RSVP: Maybe',
    color: 'text-yellow-400',
  },
  bounced: {
    icon: AlertTriangle,
    label: 'Bounced',
    color: 'text-red-400',
  },
  failed: {
    icon: AlertTriangle,
    label: 'Failed',
    color: 'text-red-400',
  },
  reminder_sent: {
    icon: Bell,
    label: 'Reminder Sent',
    color: 'text-blue-400',
  },
};

function getEventConfig(eventType: string): EventConfig {
  return eventConfig[eventType] || {
    icon: Clock,
    label: eventType,
    color: 'text-dark-muted',
  };
}

// ============================================================
// DATE FORMATTING
// ============================================================

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ============================================================
// TIMELINE EVENT ITEM
// ============================================================

interface TimelineEventProps {
  event: InvitationEvent;
  isLast: boolean;
}

function TimelineEvent({ event, isLast }: TimelineEventProps) {
  const config = getEventConfig(event.event_type);
  const Icon = config.icon;

  return (
    <div className="relative flex gap-3">
      {/* Vertical line connector */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 bottom-0 w-px bg-dark-border/40" />
      )}

      {/* Icon circle */}
      <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-dark-surface border border-dark-border/40 flex items-center justify-center ${config.color}`}>
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${config.color}`}>
            {config.label}
          </span>
          <span className="text-xs text-dark-muted">
            {formatDateTime(event.occurred_at)}
          </span>
        </div>

        {/* Metadata */}
        {event.metadata && Object.keys(event.metadata).length > 0 && (
          <div className="mt-1 text-xs text-dark-muted space-y-0.5">
            {event.metadata.respondent_name && (
              <p>Respondent: {String(event.metadata.respondent_name)}</p>
            )}
            {event.metadata.message && (
              <p>Message: {String(event.metadata.message)}</p>
            )}
            {event.metadata.click_url && (
              <p>URL: {String(event.metadata.click_url)}</p>
            )}
            {event.metadata.reminder_subject && (
              <p>Subject: {String(event.metadata.reminder_subject)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// INFO GRID ITEM
// ============================================================

function InfoItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-dark-muted mb-0.5">{label}</dt>
      <dd className="text-sm text-dark-text">{value || '-'}</dd>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function InvitationDetailModal({
  invitationId,
  isOpen,
  onClose,
}: InvitationDetailModalProps) {
  const [invitation, setInvitation] = useState<InvitationRecord | null>(null);
  const [events, setEvents] = useState<InvitationEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch detail when modal opens
  const fetchDetail = useCallback(async () => {
    if (!invitationId) return;

    setIsLoading(true);
    setError(null);

    try {
      const detail = await invitationTrackingApi.getInvitation(invitationId);
      setInvitation(detail.invitation);
      setEvents(detail.events);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load invitation details';
      setError(message);
      console.error('[InvitationDetailModal] Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    if (isOpen && invitationId) {
      fetchDetail();
    } else {
      // Reset state when closing
      setInvitation(null);
      setEvents([]);
      setError(null);
    }
  }, [isOpen, invitationId, fetchDetail]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative bg-dark-bg rounded-xl shadow-2xl shadow-black/20 w-full max-w-2xl mx-4 border border-dark-border/30 max-h-[85vh] flex flex-col"
          >
            {/* ====================== Header ====================== */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border/30 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-brand-500/20 text-brand-500 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  {isLoading ? (
                    <div className="space-y-1">
                      <div className="h-4 w-40 bg-dark-surface rounded animate-pulse" />
                      <div className="h-3 w-56 bg-dark-surface rounded animate-pulse" />
                    </div>
                  ) : invitation ? (
                    <>
                      <h2 className="text-lg font-semibold text-white truncate">
                        {invitation.recipient_name || invitation.recipient_email}
                      </h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        {invitation.recipient_name && (
                          <span className="text-xs text-dark-muted truncate">
                            {invitation.recipient_email}
                          </span>
                        )}
                        {invitation.recipient_org && (
                          <span className="text-xs text-dark-muted">
                            - {invitation.recipient_org}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <h2 className="text-lg font-semibold text-white">
                      Invitation Detail
                    </h2>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {invitation && (
                  <>
                    <InvitationStatusBadge status={invitation.status} />
                    <InvitationTypeBadge type={invitation.invitation_type} />
                  </>
                )}
                <button
                  onClick={onClose}
                  className="ml-2 text-dark-muted hover:text-dark-text transition-all duration-150"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* ====================== Content ====================== */}
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Loading */}
              {isLoading && (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
                </div>
              )}

              {/* Error */}
              {error && !isLoading && (
                <div className="text-center py-12 space-y-3">
                  <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
                  <p className="text-dark-muted text-sm">{error}</p>
                  <Button variant="secondary" size="sm" onClick={fetchDetail}>
                    Try Again
                  </Button>
                </div>
              )}

              {/* Detail content */}
              {!isLoading && !error && invitation && (
                <>
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-dark-surface/50 rounded-lg border border-dark-border/20">
                    <InfoItem label="Subject" value={invitation.subject} />
                    <InfoItem
                      label="Sent At"
                      value={formatDateTime(invitation.sent_at)}
                    />
                    <InfoItem
                      label="Event Type"
                      value={
                        <span className="uppercase text-brand-400 font-medium">
                          {invitation.event_type}
                        </span>
                      }
                    />
                    <InfoItem
                      label="Invitation Type"
                      value={<InvitationTypeBadge type={invitation.invitation_type} />}
                    />
                    <InfoItem
                      label="Resend Message ID"
                      value={
                        invitation.resend_message_id ? (
                          <span className="font-mono text-xs truncate block max-w-[200px]" title={invitation.resend_message_id}>
                            {invitation.resend_message_id}
                          </span>
                        ) : null
                      }
                    />
                    <InfoItem
                      label="Created"
                      value={formatDateTime(invitation.created_at)}
                    />
                  </div>

                  {/* Event Timeline */}
                  <div>
                    <h3 className="text-sm font-medium text-dark-text mb-4">
                      Event Timeline
                    </h3>

                    {events.length > 0 ? (
                      <div className="pl-1">
                        {events.map((event, index) => (
                          <TimelineEvent
                            key={event.id}
                            event={event}
                            isLast={index === events.length - 1}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-dark-muted">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No lifecycle events recorded yet.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ====================== Footer ====================== */}
            <div className="px-6 py-4 border-t border-dark-border/30 shrink-0 flex justify-end">
              <Button variant="secondary" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default InvitationDetailModal;

